import type { DeliveryEstimateDateRange } from "@redotech/redo-conversion-core-common/conversion/conversion-autocheck";
import {
  EDD_DEFAULT_TEMPLATE,
  tokenizeEddTemplate,
  type EddTemplateToken,
} from "@redotech/redo-conversion-core-common/conversion/edd-custom-copy";
import type { EddCustomCopy } from "@redotech/redo-conversion-core-common/conversion/storefront-delivery-estimate";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { Flex } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { Fragment, memo, useContext, useMemo, useState } from "react";
import * as css from "./delivery-estimate.module.css";
import { ZipCodeContext } from "./zip-code-context";
import { ZipCodeModal } from "./zip-code-modal";

export const DeliveryEstimate = memo(function DeliveryEstimate({
  customCopy,
  estimatedDeliveryDate,
  estimatedDeliveryDateRange,
}: {
  customCopy?: EddCustomCopy;
  estimatedDeliveryDate: string;
  estimatedDeliveryDateRange?: DeliveryEstimateDateRange;
}) {
  const zipContext = useContext(ZipCodeContext);
  const [modalOpen, setModalOpen] = useState(false);

  const customerLocale =
    globalThis.Shopify?.locale ?? navigator.language ?? "en-US";
  const formattedEstimatedDeliveryDate = formatEstimatedDeliveryDate({
    customerLocale,
    estimatedDeliveryDate,
    estimatedDeliveryDateRange,
  });

  const titleTokens = useMemo(
    () => tokenizeEddTemplate(customCopy?.title?.trim() || ""),
    [customCopy?.title],
  );
  const descriptionTokens = useMemo(
    () => tokenizeEddTemplate(customCopy?.description?.trim() || ""),
    [customCopy?.description],
  );
  const defaultTokens = useMemo(
    () => tokenizeEddTemplate(EDD_DEFAULT_TEMPLATE),
    [],
  );

  if (zipContext?.outsideUnitedStates) {
    return null;
  }

  const renderTokens = (tokens: EddTemplateToken[]) =>
    tokens.map((token, index) => (
      <Fragment key={index}>
        {renderToken({
          token,
          formattedEstimatedDeliveryDate,
          zipCode: zipContext?.savedZipCode,
          onZipClick: () => setModalOpen(true),
        })}
      </Fragment>
    ));

  const hasCustomCopy = titleTokens.length > 0 || descriptionTokens.length > 0;

  return (
    <>
      <Flex align="center" gap="md" my="xl">
        <TruckIcon className={css.truckIcon} />
        {hasCustomCopy ? (
          <Flex className={css.text} dir="column" gap="none">
            {titleTokens.length > 0 && (
              <span className={css.title}>{renderTokens(titleTokens)}</span>
            )}
            {descriptionTokens.length > 0 && (
              <span>{renderTokens(descriptionTokens)}</span>
            )}
          </Flex>
        ) : (
          <span className={css.text}>{renderTokens(defaultTokens)}</span>
        )}
      </Flex>
      <ZipCodeModal onClose={() => setModalOpen(false)} open={modalOpen} />
    </>
  );
});

function renderToken({
  token,
  formattedEstimatedDeliveryDate,
  zipCode,
  onZipClick,
}: {
  token: EddTemplateToken;
  formattedEstimatedDeliveryDate: string;
  zipCode: string | undefined;
  onZipClick: () => void;
}) {
  if (token.type === "text") {
    return token.value;
  }
  switch (token.name) {
    case "delivery_date":
      return (
        <span className={css.estimate}>{formattedEstimatedDeliveryDate}</span>
      );
    case "to_zip_code":
      if (!zipCode) {
        return null;
      }
      return (
        <>
          {" to "}
          <span className={css.zipCode} onClick={onZipClick}>
            {zipCode}
          </span>
        </>
      );
    default:
      assertNever(token.name);
  }
}

function formatEstimatedDeliveryDate({
  customerLocale,
  estimatedDeliveryDate,
  estimatedDeliveryDateRange,
}: {
  customerLocale: string;
  estimatedDeliveryDate: string;
  estimatedDeliveryDateRange: DeliveryEstimateDateRange | undefined;
}) {
  if (!estimatedDeliveryDateRange) {
    return formatDate({ customerLocale, isoDate: estimatedDeliveryDate });
  }

  const formattedStartDate = formatDate({
    customerLocale,
    isoDate: estimatedDeliveryDateRange.start,
  });
  const formattedEndDate = formatDate({
    customerLocale,
    isoDate: estimatedDeliveryDateRange.end,
  });

  if (formattedStartDate === formattedEndDate) {
    return formattedStartDate;
  }

  return `${formattedStartDate} - ${formattedEndDate}`;
}

function formatDate({
  customerLocale,
  isoDate,
}: {
  customerLocale: string;
  isoDate: string;
}) {
  return new Date(isoDate).toLocaleDateString(customerLocale, {
    month: "long",
    day: "numeric",
  });
}
